import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { getApiBaseUrl } from './utils/api';
import Icon from './Icon';
import './Tickets.css';

const createInitialTicketForm = (overrides = {}) => ({
  requester: '',
  subject: '',
  details: '',
  impactedAsset: '',
  ...overrides
});

const Tickets = ({ initialFilters = {}, statusWhitelist = null, createdSinceDays = null, hideClosedSolved = true, excludeOffered = false }) => {
  const { selectedTenant, authenticatedFetch, user, hasPrivilege } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [columns, setColumns] = useState([]);
  const [zendeskTenant, setZendeskTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'priority', direction: 'asc' });
  const [searchFilters, setSearchFilters] = useState({});
  const [showCreateTicketForm, setShowCreateTicketForm] = useState(false);

  const defaultRequester = useMemo(() => {
    const candidates = [
      user?.email,
      user?.Email,
      user?.upn,
      user?.UPN,
      user?.username,
      user?.Username
    ];
    const identity = candidates.find(value => typeof value === 'string' && value.trim());
    return identity ? identity.trim() : '';
  }, [user]);

  const [ticketForm, setTicketForm] = useState(() => createInitialTicketForm({ requester: defaultRequester }));
  const [ticketFormErrors, setTicketFormErrors] = useState({});
  const [ticketSubmitResult, setTicketSubmitResult] = useState(null);
  const [isSubmittingTicket, setIsSubmittingTicket] = useState(false);
  const [assetSuggestions, setAssetSuggestions] = useState([]);
  const [isLoadingAssetSuggestions, setIsLoadingAssetSuggestions] = useState(false);

  const fetchTickets = useCallback(async () => {
    if (!selectedTenant) {
      setError('No tenant selected');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Sync CBC orders first (fail silently if no API key configured)
      console.log('[Tickets] Starting order sync for tenant:', selectedTenant.name);
      try {
        const syncUrl = `${getApiBaseUrl()}/api/tickets/sync-orders`;
        console.log('[Tickets] Calling sync-orders endpoint:', syncUrl);
        const syncResponse = await authenticatedFetch(syncUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            tenant: selectedTenant.name
          })
        });
        
        console.log('[Tickets] Sync response status:', syncResponse.status, syncResponse.ok);
        if (syncResponse.ok) {
          const syncData = await syncResponse.json();
          console.log('[Tickets] Order sync completed:', syncData.message);
        } else {
          const errorData = await syncResponse.json().catch(() => ({ error: 'Unknown error' }));
          console.log('[Tickets] Order sync failed:', errorData);
        }
      } catch (syncError) {
        // Fail silently - order sync is optional
        console.error('[Tickets] Order sync error:', syncError);
      }
      
      // Build server-side filters if provided
      const params = new URLSearchParams({ tenant: selectedTenant.name });
      if (createdSinceDays && createdSinceDays > 0) {
        params.append('sinceDays', String(createdSinceDays));
      }
      if (Array.isArray(statusWhitelist) && statusWhitelist.length > 0) {
        params.append('status', statusWhitelist.join(','));
      }
      if (excludeOffered) {
        params.append('excludeOffered', 'true');
      }

      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/tickets?${params.toString()}`);

      const data = await response.json();

      if (response.ok) {
        setTickets(data.tickets);
        setColumns(data.columns);
        setZendeskTenant(data.zendeskTenant);
      } else {
        setError(data.error || 'Failed to fetch tickets');
      }
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedTenant, authenticatedFetch, createdSinceDays, statusWhitelist, excludeOffered]);

  useEffect(() => {
    if (selectedTenant) {
      fetchTickets();
    }
  }, [selectedTenant, fetchTickets]);

  // Apply initial text filters if provided
  useEffect(() => {
    if (initialFilters && Object.keys(initialFilters).length > 0) {
      setSearchFilters(prev => ({ ...prev, ...initialFilters }));
    }
  }, [initialFilters]);

  useEffect(() => {
    if (!showCreateTicketForm || !selectedTenant) {
      setAssetSuggestions([]);
      setIsLoadingAssetSuggestions(false);
      return;
    }

    const query = ticketForm.impactedAsset?.trim() || '';
    if (query.length < 2) {
      setAssetSuggestions([]);
      setIsLoadingAssetSuggestions(false);
      return;
    }

    let isCancelled = false;
    setIsLoadingAssetSuggestions(true);

    const timeoutId = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          tenant: selectedTenant.name,
          q: query,
          limit: '10'
        });
        const response = await authenticatedFetch(`${getApiBaseUrl()}/api/assets/search?${params.toString()}`);
        const data = await response.json();

        if (!isCancelled) {
          if (response.ok && Array.isArray(data.assets)) {
            setAssetSuggestions(data.assets);
          } else {
            setAssetSuggestions([]);
          }
        }
      } catch (fetchError) {
        if (!isCancelled) {
          console.error('Error fetching asset suggestions:', fetchError);
          setAssetSuggestions([]);
        }
      } finally {
        if (!isCancelled) {
          setIsLoadingAssetSuggestions(false);
        }
      }
    }, 350);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [ticketForm.impactedAsset, showCreateTicketForm, selectedTenant, authenticatedFetch]);

  const formatValue = (value, column) => {
    if (value === null || value === undefined) {
      return <span className="null-value">-</span>;
    }
    
    // Special handling for Zendesk-ID field
    const fieldName = column.field.toLowerCase();
    if (fieldName === 'zendesk_id' || fieldName === 'zendesk-id') {
      const zendeskId = value.toString();
      // Construct the full Zendesk URL using the tenant from the database
      if (zendeskTenant) {
        const zendeskUrl = `https://${zendeskTenant}/requests/${zendeskId}`;
        
        return (
          <a 
            href={zendeskUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="zendesk-link"
            onClick={(e) => e.stopPropagation()}
            title={`Open ticket ${zendeskId} in Zendesk`}
          >
            {zendeskId}
          </a>
        );
      } else {
        // Fallback if no Zendesk tenant is configured
        return (
          <span className="zendesk-id-no-config" title="No Zendesk tenant configured">
            {zendeskId}
          </span>
        );
      }
    }
    
    // Format based on column type
    if (column.type && column.type.includes('int')) {
      return value.toString();
    }
    
    if (column.type && column.type.includes('date')) {
      return new Date(value).toLocaleDateString();
    }
    
    if (column.type && column.type.includes('datetime')) {
      return new Date(value).toLocaleString();
    }
    
    return value.toString();
  };

  const getColumnDisplayName = (fieldName) => {
    // Convert field names to display names
    const displayNames = {
      'id': 'ID',
      'title': 'Title',
      'description': 'Description',
      'status': 'Status',
      'priority': 'Priority',
      'assigned_to': 'Assigned To',
      'created_by': 'Created By',
      'created_at': 'Created At',
      'updated_at': 'Updated At',
      'due_date': 'Due Date',
      'category': 'Category',
      'type': 'Type',
      'zendesk_id': 'Zendesk-ID',
      'zendesk-id': 'Zendesk-ID'
    };
    
    return displayNames[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Filter out ID column and unwanted columns, then sort columns
  const filteredColumns = columns.filter(column => {
    const fieldName = column.field.toLowerCase();
    return fieldName !== 'id' && fieldName !== 'zendesk-raw' && fieldName !== 'satisfaction_rating' && fieldName !== 'order';
  });

  // Handle search filter changes
  const handleSearchChange = (columnField, value) => {
    setSearchFilters(prev => ({
      ...prev,
      [columnField]: value.toLowerCase()
    }));
  };

  // Clear all search filters
  const clearAllFilters = () => {
    setSearchFilters({});
  };

  useEffect(() => {
    setTicketForm(prev => {
      if (prev.requester && prev.requester.trim()) {
        return prev;
      }
      return {
        ...prev,
        requester: defaultRequester
      };
    });
  }, [defaultRequester]);

  const resetTicketForm = (overrides = {}) => {
    setTicketForm(createInitialTicketForm({ requester: defaultRequester, ...overrides }));
    setTicketFormErrors({});
    setTicketSubmitResult(null);
    setAssetSuggestions([]);
    setIsSubmittingTicket(false);
  };

  const openTicketForm = () => {
    resetTicketForm();
    setShowCreateTicketForm(true);
  };

  const closeTicketForm = () => {
    setShowCreateTicketForm(false);
    resetTicketForm();
  };

  const getAssetDisplayLabel = (asset) => {
    if (!asset || typeof asset !== 'object') return '';

    const serial = asset.serial || asset.Serial || asset.SERIAL;
    const name = asset.name || asset.Name || asset.hostname || asset.Hostname || asset['device-name'] || asset['device_name'] || asset.model || asset.Model;
    const user = asset.username || asset.Username || asset.assigned_to || asset.assignedTo;

    const parts = [];
    if (serial) parts.push(serial);
    if (name) parts.push(name);
    if (user) parts.push(user);

    return parts.join(' • ');
  };

  const handleTicketInputChange = (field, value) => {
    setTicketForm(prev => ({
      ...prev,
      [field]: value
    }));
    setTicketFormErrors(prev => ({
      ...prev,
      [field]: undefined
    }));
    if (field === 'impactedAsset' && value.length < 2) {
      setAssetSuggestions([]);
    }
  };

  const handleAssetSuggestionSelect = (asset) => {
    const label = getAssetDisplayLabel(asset);
    setTicketForm(prev => ({
      ...prev,
      impactedAsset: label || prev.impactedAsset
    }));
    setAssetSuggestions([]);
  };

  const validateTicketForm = () => {
    const errors = {};
    if (!ticketForm.requester.trim()) {
      errors.requester = 'Requester is required';
    }
    if (!ticketForm.subject.trim()) {
      errors.subject = 'Subject is required';
    }
    if (!ticketForm.details.trim()) {
      errors.details = 'Ticket details are required';
    }
    setTicketFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleTicketSubmit = async (event) => {
    event.preventDefault();
    if (!selectedTenant || !validateTicketForm()) {
      return;
    }

    setIsSubmittingTicket(true);
    setTicketSubmitResult(null);

    const payload = {
      tenant: selectedTenant.name,
      requester: ticketForm.requester.trim(),
      subject: ticketForm.subject.trim(),
      details: ticketForm.details.trim(),
      impactedAsset: ticketForm.impactedAsset?.trim() || null
    };

    try {
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/tickets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      let data = {};
      try {
        data = await response.json();
      } catch (_) {
        data = {};
      }

      if (response.ok) {
        setTicketSubmitResult({
          success: true,
          message: data.message || 'Ticket created successfully',
          ticketUrl: data.ticketUrl,
          ticketId: data.ticketId
        });
        fetchTickets();
        setTicketForm(createInitialTicketForm());
      } else {
        setTicketSubmitResult({
          success: false,
          message: data.error || 'Failed to create ticket. Please try again.'
        });
      }
    } catch (submitError) {
      console.error('Error submitting ticket:', submitError);
      setTicketSubmitResult({
        success: false,
        message: 'Network error. Please try again.'
      });
    } finally {
      setIsSubmittingTicket(false);
    }
  };

  // Filter and sort tickets based on search filters and sort configuration
  const filteredAndSortedTickets = [...tickets]
    .filter(item => {
      // Status filtering
      const rawStatus = item.status || item.Status || '';
      const status = String(rawStatus).toLowerCase();

      if (Array.isArray(statusWhitelist) && statusWhitelist.length > 0) {
        if (!statusWhitelist.includes(status)) return false;
      } else if (hideClosedSolved) {
        if (status === 'closed' || status === 'solved') return false;
      }

      // Date filtering (created_at within last N days)
      if (createdSinceDays && createdSinceDays > 0) {
        const createdVal = item.created_at || item.createdAt || item.CreatedDateTime;
        if (!createdVal) return false;
        const createdDate = new Date(createdVal);
        if (isNaN(createdDate.getTime())) return false;
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - createdSinceDays);
        if (createdDate < cutoff) return false;
      }

      // Apply text search filters
      return Object.entries(searchFilters).every(([columnField, searchValue]) => {
        if (!searchValue) return true; // No filter applied for this column
        const itemValue = item[columnField];
        if (itemValue === null || itemValue === undefined) return false;
        return String(itemValue).toLowerCase().includes(String(searchValue).toLowerCase());
      });
    })
    .sort((a, b) => {
      if (!sortConfig.key) return 0;
      
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;
      
      // Special handling for priority field (urgent first)
      if (sortConfig.key === 'priority') {
        const priorityOrder = { 'urgent': 1, 'high': 2, 'medium': 3, 'low': 4 };
        const aPriority = priorityOrder[aValue.toLowerCase()] || 5;
        const bPriority = priorityOrder[bValue.toLowerCase()] || 5;
        return sortConfig.direction === 'asc' ? aPriority - bPriority : bPriority - aPriority;
      }
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      // Convert to string for comparison
      const aStr = String(aValue);
      const bStr = String(bValue);
      return sortConfig.direction === 'asc' 
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });

  // Handle column sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Calculate age-based color for tickets
  const getTicketAgeColor = (createdAt) => {
    if (!createdAt) return '';
    
    const createdDate = new Date(createdAt);
    const now = new Date();
    const daysDiff = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
    
    if (daysDiff > 30) return 'ticket-old'; // Red for >30 days
    if (daysDiff > 10) return 'ticket-warning'; // Yellow for >10 days
    return ''; // Normal color for <=10 days
  };

  // Calculate priority counts
  const getPriorityCounts = () => {
    const counts = {
      urgent: 0,
      high: 0,
      medium: 0,
      low: 0,
      'no priority': 0
    };

    tickets.forEach(ticket => {
      const priority = ticket.priority ? ticket.priority.toLowerCase() : 'no priority';
      if (counts.hasOwnProperty(priority)) {
        counts[priority]++;
      } else {
        counts['no priority']++;
      }
    });

    return counts;
  };

  if (loading) {
    return (
      <div className="tickets-container">
        <div className="tickets-header">
          <h2>Tickets</h2>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading tickets...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="tickets-container">
        <div className="tickets-header">
          <h2>Tickets</h2>
        </div>
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h3>Error Loading Tickets</h3>
          <p>{error}</p>
          <button onClick={fetchTickets} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!selectedTenant) {
    return (
      <div className="tickets-container">
        <div className="tickets-header">
          <h2>Tickets</h2>
        </div>
        <div className="no-tenant-container">
          <div className="no-tenant-icon">🏢</div>
          <h3>No Tenant Selected</h3>
          <p>Please select a tenant from the dropdown to view tickets.</p>
        </div>
      </div>
    );
  }

  return (
      <div className="tickets-container">
        <div className="tickets-header">
          <div className="tickets-title-section">
            <h2>Tickets</h2>
            <div className="priority-counts">
              {(() => {
                const counts = getPriorityCounts();
                return (
                  <>
                    {counts.urgent > 0 && <span className="priority-count urgent">Urgent: {counts.urgent}</span>}
                    {counts.high > 0 && <span className="priority-count high">High: {counts.high}</span>}
                    {counts.medium > 0 && <span className="priority-count medium">Medium: {counts.medium}</span>}
                    {counts.low > 0 && <span className="priority-count low">Low: {counts.low}</span>}
                    {counts['no priority'] > 0 && <span className="priority-count no-priority">No Priority: {counts['no priority']}</span>}
                  </>
                );
              })()}
            </div>
          </div>
          <div className="tickets-actions">
            <button onClick={fetchTickets} className="refresh-button">
              <Icon name="refresh" size="small" />
              Refresh
            </button>
            {hasPrivilege('tickets', 'create') && (
              <button onClick={openTicketForm} className="create-ticket-button">
                <Icon name="plus" size="small" />
                Create Ticket
              </button>
            )}
            {Object.keys(searchFilters).length > 0 && (
              <button onClick={clearAllFilters} className="clear-filters-button">
                Clear Filters
              </button>
            )}
            <span className="tickets-count">
              {filteredAndSortedTickets.length} of {tickets.length} ticket{tickets.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

      {tickets.length === 0 ? (
        <div className="no-tickets-container">
          <Icon name="tickets" size="large" className="no-tickets-icon" />
          <h3>No Tickets Found</h3>
          <p>No tickets found for tenant "{selectedTenant.name}".</p>
        </div>
      ) : (
        <div className="tickets-grid-container">
          <div className="tickets-grid">
            <table className="tickets-table">
              <thead>
                <tr>
                  {filteredColumns.map((column) => (
                    <th 
                      key={column.field} 
                      className="table-header sortable"
                      onClick={() => handleSort(column.field)}
                    >
                      <div className="header-content">
                        <span>{getColumnDisplayName(column.field)}</span>
                        {sortConfig.key === column.field && (
                          <span className="sort-indicator">
                            {sortConfig.direction === 'asc' ? '↑' : '↓'}
                          </span>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
                <tr className="search-row">
                  {filteredColumns.map((column) => (
                    <th key={`search-${column.field}`} className="search-header">
                      <input
                        type="text"
                        placeholder={`Search ${getColumnDisplayName(column.field)}...`}
                        value={searchFilters[column.field] || ''}
                        onChange={(e) => handleSearchChange(column.field, e.target.value)}
                        className="column-search-input"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedTickets.map((ticket, index) => (
                  <tr key={ticket.id || index} className={`table-row ${getTicketAgeColor(ticket.created_at)}`}>
                    {filteredColumns.map((column) => {
                      const cellValue = ticket[column.field];
                      const hasValue = cellValue && cellValue !== '';
                      
                      const handleCellClick = () => {
                        if (hasValue) {
                          // Set filter for this column
                          handleSearchChange(column.field, cellValue);
                        }
                      };
                      
                      return (
                        <td 
                          key={column.field} 
                          className={`table-cell ${hasValue ? 'clickable-cell' : ''}`}
                          onClick={handleCellClick}
                          style={{ 
                            cursor: hasValue ? 'pointer' : 'default' 
                          }}
                        >
                          {formatValue(ticket[column.field], column)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {showCreateTicketForm && (
        <div className="ticket-form-overlay">
          <div className="ticket-form-modal">
            <div className="ticket-form-header">
              <h3>Create Ticket</h3>
              <button className="ticket-form-close" onClick={closeTicketForm} aria-label="Close ticket form">
                X
              </button>
            </div>
            <form className="ticket-form" onSubmit={handleTicketSubmit}>
              <div className="ticket-form-field">
                <label htmlFor="ticket-requester">Requester</label>
                <input
                  id="ticket-requester"
                  type="text"
                  value={ticketForm.requester}
                  onChange={(e) => handleTicketInputChange('requester', e.target.value)}
                  placeholder="Name or email of the requester"
                />
                {ticketFormErrors.requester && <span className="ticket-form-error">{ticketFormErrors.requester}</span>}
              </div>
              <div className="ticket-form-field">
                <label htmlFor="ticket-subject">Subject</label>
                <input
                  id="ticket-subject"
                  type="text"
                  value={ticketForm.subject}
                  onChange={(e) => handleTicketInputChange('subject', e.target.value)}
                  placeholder="Brief summary of the issue"
                />
                {ticketFormErrors.subject && <span className="ticket-form-error">{ticketFormErrors.subject}</span>}
              </div>
              <div className="ticket-form-field">
                <label htmlFor="ticket-details">Ticket details</label>
                <textarea
                  id="ticket-details"
                  value={ticketForm.details}
                  onChange={(e) => handleTicketInputChange('details', e.target.value)}
                  placeholder="Describe the issue or request"
                  rows={5}
                />
                {ticketFormErrors.details && <span className="ticket-form-error">{ticketFormErrors.details}</span>}
              </div>
              <div className="ticket-form-field">
                <label htmlFor="ticket-asset">Impacted asset (optional)</label>
                <div className="ticket-form-typeahead">
                  <input
                    id="ticket-asset"
                    type="text"
                    value={ticketForm.impactedAsset}
                    onChange={(e) => handleTicketInputChange('impactedAsset', e.target.value)}
                    placeholder="Search by serial, hostname, model, or user"
                  />
                  {isLoadingAssetSuggestions && <span className="typeahead-status">Searching...</span>}
                  {!isLoadingAssetSuggestions && ticketForm.impactedAsset.length >= 2 && assetSuggestions.length === 0 && (
                    <span className="typeahead-status muted">No matching assets</span>
                  )}
                  {assetSuggestions.length > 0 && (
                    <ul className="typeahead-suggestions">
                      {assetSuggestions.map((asset, index) => (
                        <li key={`${asset.id || asset.serial || index}-${index}`}>
                          <button type="button" onClick={() => handleAssetSuggestionSelect(asset)}>
                            {getAssetDisplayLabel(asset) || 'Unnamed asset'}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              {ticketSubmitResult && (
                <div className={`ticket-form-result ${ticketSubmitResult.success ? 'success' : 'error'}`}>
                  <p>{ticketSubmitResult.message}</p>
                  {ticketSubmitResult.success && ticketSubmitResult.ticketUrl && (
                    <a href={ticketSubmitResult.ticketUrl} target="_blank" rel="noopener noreferrer">
                      Open ticket #{ticketSubmitResult.ticketId || ''}
                    </a>
                  )}
                </div>
              )}
              <div className="ticket-form-actions">
                <button type="button" className="secondary" onClick={closeTicketForm}>
                  Cancel
                </button>
                <button type="submit" disabled={isSubmittingTicket}>
                  {isSubmittingTicket ? 'Creating...' : 'Create Ticket'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tickets;
